import React from 'react'
import { useAppSelector } from '../redux/hooks'
import User, { RoleType } from '../models/User'
import { selectUserData } from '../redux/actions/login/loginSlice'
import { usePermissions } from '../hooks/usePermissions'
import { AdminPermission, guardRoute } from './adminRouteHelpers'
import {
  getFirstAllowedRoute,
  resolveAdminPanelPath,
  isRbacStaff,
} from '../utils/permissionUtil'
import { Navigate } from 'react-router-dom'

const ActiveMarkets = React.lazy(() => import('../admin-app/pages/active-matches/active-markets'))
const ActiveMatches = React.lazy(() => import('../admin-app/pages/active-matches/active-matches'))
const GetAllFancy = React.lazy(() => import('../admin-app/pages/active-matches/get-all-fancy'))
const MatchesPage = React.lazy(() => import('../admin-app/pages/add-matches/matches'))
const SeriesPage = React.lazy(() => import('../admin-app/pages/add-matches/series'))
const SportsPage = React.lazy(() => import('../admin-app/pages/add-matches/sports'))
const AddUser = React.lazy(() => import('../admin-app/pages/add-user/add-user'))
const ListClients = React.lazy(() => import('../admin-app/pages/list-clients/list-clients'))
const MainAdmin = React.lazy(() => import('../admin-app/pages/_layout/MainAdmin'))
const Message = React.lazy(() => import('../admin-app/pages/settings/message'))
const WhatsAppSettings = React.lazy(() => import('../admin-app/pages/settings/whatsapp-settings'))
const Paymethod = React.lazy(() => import('../admin-app/pages/settings/paymethod'))
const UndeclaredBets = React.lazy(() => import('../admin-app/pages/undeclared-bets/undeclared-bets'))
const SeriesSettings = React.lazy(() => import('../admin-app/pages/settings/series-settings'))
const MatchSetting = React.lazy(() => import('../admin-app/pages/match-setting/match-setting'))
const AdminDashboard = React.lazy(
  () => import('../admin-app/pages/admin-dashboard/admin-dashboard'),
)
const AccountStatementAdmin = React.lazy(
  () => import('../admin-app/pages/AccountStatement/AccountStatementAdmin'),
)
const DepositStatementAdmin = React.lazy(
  () => import('../admin-app/pages/transaction-statement/DepositStatementAdmin'),
)
const WithdrawStatementAdmin = React.lazy(
  () => import('../admin-app/pages/transaction-statement/WithdrawStatementAdmin'))
const ProfitLossAdmin = React.lazy(() => import('../admin-app/pages/PlReport/ProfitLossAdmin'))
const UnsetteleBetHistoryAdmin = React.lazy(
  () => import('../admin-app/pages/UnsetteleBetHistory/UnsetteleBetHistoryAdmin'),
)
const Odds = React.lazy(() => import('../pages/odds/odds'))
const ChangePassword = React.lazy(() => import('../pages/ChangePassword/ChangePassword'))
const AuthLayout = React.lazy(() => import('../pages/_layout/AuthLayout'))
const Login = React.lazy(() => import('./pages/login/login'))
const CasinoWrapper = React.lazy(() => import('../pages/CasinoList/CasinoWrapper'))
const CasinoList = React.lazy(() => import('./pages/casino-list/casino-list'))
const GameReportAdmin = React.lazy(() => import('./pages/GameReports/GameReportAdmin'))
const LedgerAdmin = React.lazy(() => import('../admin-app/pages/Ledger/LedgerAdmin'))
const UserBookAdmin = React.lazy(() => import('../admin-app/pages/UserBook/UserBookAdmin'))
const Rules = React.lazy(() => import('./pages/Rules/rules'))
const Tc = React.lazy(() => import('./pages/Rules/tc'))
const AnalysisProfitloss = React.lazy(() => import('./pages/Anaysis-pl/AnalysisProfitloss'))
const InplayMatch = React.lazy(() => import('./pages/InplayMatch/InplayMatch'))
const SecurityMonitoring = React.lazy(() => import('./pages/security-monitoring/SecurityMonitoring'))
const BetDelay = React.lazy(() => import('./pages/settings/bet-delay'))
const CasinoVisibility = React.lazy(() => import('./pages/casino-visibility/CasinoVisibility'))
const AffiliateManagement = React.lazy(() => import('./pages/affiliate-management/AffiliateManagement'))
const BonusAdmin = React.lazy(() => import('./pages/bonus/BonusAdmin'))
const RbacUsers = React.lazy(() => import('./pages/rbac-users/RbacUsers'))
const Unauthorized = React.lazy(() => import('./pages/unauthorized/Unauthorized'))
const Bat = React.lazy(() => import('../pages/bat/Bat'))

const RbacHomeRedirect = () => {
  const userState = useAppSelector<{ user: User }>(selectUserData)
  return <Navigate to={resolveAdminPanelPath(getFirstAllowedRoute(userState.user))} replace />
}

const AdminHome = () => {
  const userState = useAppSelector<{ user: User }>(selectUserData)
  // Trueplay home is Event Management (InplayMatch). RBAC without that permission goes to first allowed page.
  if (
    isRbacStaff(userState.user) &&
    !userState.user.permissions?.includes(AdminPermission.EVENT_MANAGEMENT)
  ) {
    return <RbacHomeRedirect />
  }
  return <InplayMatch />
}

const AdminRoutes = () => {
  const userState = useAppSelector<{ user: User }>(selectUserData)
  const { hasPermission, isFullAdmin } = usePermissions()
  const can = (p: keyof typeof AdminPermission) =>
    isFullAdmin || hasPermission(AdminPermission[p])

  const superAdminRoutes =
    userState.user.role === RoleType.admin || userState.user.role === RoleType.sadmin
      ? [
          { path: 'sports-list/:url?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <SportsPage />) },
          { path: 'series/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <SeriesPage />) },
          { path: 'matches/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <MatchesPage />) },
          { path: 'active-matches/:sportId/:matchType?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMatches />) },
          { path: 'active-markets/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMarkets />) },
          { path: 'active-fancies/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <GetAllFancy />) },
          { path: 'undeclared-bets', element: guardRoute(AdminPermission.RESULT_DECLARATION, <UndeclaredBets />) },
          { path: 'messages', element: guardRoute(AdminPermission.MESSAGES, <Message />) },
          { path: 'whatsapp-settings', element: guardRoute(AdminPermission.WHATSAPP, <WhatsAppSettings />) },
          { path: 'series-settings', element: guardRoute(AdminPermission.SERIES_SETTINGS, <SeriesSettings />) },
          { path: 'betdelay', element: guardRoute(AdminPermission.BET_DELAY, <BetDelay />) },
        ]
      : []

  const rbacSettingsRoutes = isFullAdmin
    ? []
    : [
        ...(can('BLOCK_MARKETS')
          ? [
              { path: 'sports-list/:url?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <SportsPage />) },
              { path: 'active-matches/:sportId/:matchType?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMatches />) },
              { path: 'active-markets/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMarkets />) },
              { path: 'active-fancies/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <GetAllFancy />) },
            ]
          : []),
        ...(can('ADD_MATCH')
          ? [
              { path: 'series/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <SeriesPage />) },
              { path: 'matches/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <MatchesPage />) },
            ]
          : []),
        ...(can('RESULT_DECLARATION')
          ? [{ path: 'undeclared-bets', element: guardRoute(AdminPermission.RESULT_DECLARATION, <UndeclaredBets />) }]
          : []),
        ...(can('MESSAGES') ? [{ path: 'messages', element: guardRoute(AdminPermission.MESSAGES, <Message />) }] : []),
        ...(can('WHATSAPP')
          ? [{ path: 'whatsapp-settings', element: guardRoute(AdminPermission.WHATSAPP, <WhatsAppSettings />) }]
          : []),
        ...(can('SERIES_SETTINGS')
          ? [{ path: 'series-settings', element: guardRoute(AdminPermission.SERIES_SETTINGS, <SeriesSettings />) }]
          : []),
        ...(can('BET_DELAY') ? [{ path: 'betdelay', element: guardRoute(AdminPermission.BET_DELAY, <BetDelay />) }] : []),
        ...(can('MATCH_SETTINGS')
          ? [{ path: 'match-settings/:matchId', element: guardRoute(AdminPermission.MATCH_SETTINGS, <MatchSetting />) }]
          : []),
      ]

  return [
    {
      path: '/admin',
      element: <AuthLayout />,
      children: [
        { path: 'login', element: <Login /> },
        { path: 'bat', element: <Bat /> },
        {
          path: '',
          element: <MainAdmin />,
          children: [
            { index: true, element: <AdminHome /> },
            { path: 'unauthorized', element: <Unauthorized /> },
            ...(isFullAdmin
              ? [{ path: 'rbac-users', element: guardRoute(AdminPermission.RBAC_MANAGE, <RbacUsers />) }]
              : []),
            ...(can('EVENT_MANAGEMENT')
              ? [
                  { path: 'dashbaord', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <AdminDashboard />) },
                  { path: 'market-analysis', element: guardRoute(AdminPermission.MARKET_ANALYSIS, <AdminDashboard />) },
                  { path: 'inplay-matches', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <InplayMatch />) },
                  { path: 'odds/:matchId', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <Odds />) },
                ]
              : []),
            ...(can('USERS')
              ? [
                  ...['list-clients', 'list-clients/:username'].map((path) => ({
                    key: 'list-client',
                    path,
                    element: guardRoute(AdminPermission.USERS, <ListClients />),
                  })),
                  ...['add-user', 'add-user/:username'].map((path) => ({
                    key: 'add-user',
                    path,
                    element: guardRoute(AdminPermission.USERS, <AddUser />),
                  })),
                ]
              : []),
            { path: 'rules', element: <Rules /> },
            { path: 'terms', element: <Tc /> },
            ...superAdminRoutes,
            ...rbacSettingsRoutes,
            ...(can('SECURITY_MONITORING')
              ? [{ path: 'security-monitoring', element: guardRoute(AdminPermission.SECURITY_MONITORING, <SecurityMonitoring />) }]
              : []),
            { path: 'change-password', element: <ChangePassword /> },
            ...(can('ACCOUNT_STATEMENT')
              ? [{ path: 'accountstatement', element: guardRoute(AdminPermission.ACCOUNT_STATEMENT, <AccountStatementAdmin />) }]
              : []),
            ...(can('PROFIT_LOSS')
              ? [{ path: 'profitloss', element: guardRoute(AdminPermission.PROFIT_LOSS, <ProfitLossAdmin />) }]
              : []),
            ...(can('DELETED_BETS')
              ? [{ path: 'unsettledbet/deleted', element: guardRoute(AdminPermission.DELETED_BETS, <UnsetteleBetHistoryAdmin />) }]
              : []),
            ...(can('CURRENT_BETS')
              ? [
                  { path: 'unsettledbet', element: guardRoute(AdminPermission.CURRENT_BETS, <UnsetteleBetHistoryAdmin />) },
                  { path: 'unsettledbet/:type', element: guardRoute(AdminPermission.CURRENT_BETS, <UnsetteleBetHistoryAdmin />) },
                ]
              : []),
            ...(can('CASINO_LIST')
              ? [
                  { path: 'casino/:gameCode', element: guardRoute(AdminPermission.CASINO_LIST, <CasinoWrapper />) },
                  { path: 'casino-list', element: guardRoute(AdminPermission.CASINO_LIST, <CasinoList />) },
                ]
              : []),
            ...(can('CASINO_VISIBILITY')
              ? [{ path: 'casino-visibility', element: guardRoute(AdminPermission.CASINO_VISIBILITY, <CasinoVisibility />) }]
              : []),
            ...(can('GAME_REPORTS')
              ? [{ path: 'game-reports', element: guardRoute(AdminPermission.GAME_REPORTS, <GameReportAdmin />) }]
              : []),
            ...(can('DEPOSIT')
              ? [{ path: 'depositstatement', element: guardRoute(AdminPermission.DEPOSIT, <DepositStatementAdmin />) }]
              : []),
            ...(can('WITHDRAW')
              ? [{ path: 'withdrawstatement', element: guardRoute(AdminPermission.WITHDRAW, <WithdrawStatementAdmin />) }]
              : []),
            ...(can('PAYMENT_METHOD')
              ? [{ path: 'payment-method', element: guardRoute(AdminPermission.PAYMENT_METHOD, <Paymethod />) }]
              : []),
            ...(can('LEDGER') ? [{ path: 'ledger', element: guardRoute(AdminPermission.LEDGER, <LedgerAdmin />) }] : []),
            ...(can('USER_BOOK')
              ? [{ path: 'user-book', element: guardRoute(AdminPermission.USER_BOOK, <UserBookAdmin />) }]
              : []),
            ...(can('AFFILIATES')
              ? [{ path: 'affiliates', element: guardRoute(AdminPermission.AFFILIATES, <AffiliateManagement />) }]
              : []),
            ...(can('BONUS') ? [{ path: 'bonus', element: guardRoute(AdminPermission.BONUS, <BonusAdmin />) }] : []),
            ...(can('MATCH_SETTINGS')
              ? [{ path: 'match-settings/:matchId', element: guardRoute(AdminPermission.MATCH_SETTINGS, <MatchSetting />) }]
              : []),
            ...(can('ADMIN_PL')
              ? [{ path: 'analysis-pl', element: guardRoute(AdminPermission.ADMIN_PL, <AnalysisProfitloss />) }]
              : []),
          ],
        },
      ],
    },
  ]
}

export default AdminRoutes
